import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, Mail } from "lucide-react";
import { submitContact } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setStatus("sending");
    try {
      await submitContact(form);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(
        err?.response?.data?.detail ||
          "Couldn't send your message right now. Please try again in a moment."
      );
    }
  };

  return (
    <div className="bg-ink text-white min-h-screen" data-testid="contact-page">
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" data-testid="contact-back-home" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-cyan transition-colors">
          <ArrowLeft size={14} /> Back to Thortek Performance
        </Link>

        <div className="mt-8 flex items-center gap-3">
          <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-cyan/10 text-cyan">
            <Mail size={20} />
          </span>
          <div>
            <h1 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl" data-testid="contact-title">
              Contact Us
            </h1>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">
              Questions, support, or feedback
            </p>
          </div>
        </div>

        {status === "sent" ? (
          <div className="mt-8 rounded-2xl border border-cyan/30 bg-cyan/5 p-6 text-center" data-testid="contact-success">
            <CheckCircle2 size={40} className="mx-auto text-cyan" />
            <h2 className="mt-3 font-heading font-bold text-lg">Message sent</h2>
            <p className="mt-1 text-sm text-slate-400">
              Thanks for reaching out — we'll reply to your email shortly.
            </p>
            <button
              onClick={() => setStatus("idle")}
              data-testid="contact-send-another"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-sm hover:border-cyan hover:text-cyan transition-all"
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="contact-form">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Name</label>
              <input
                type="text" required value={form.name} onChange={update("name")}
                data-testid="contact-name-input" maxLength={120}
                className="w-full px-4 py-3 rounded-lg bg-surface border border-white/10 text-white placeholder-slate-600 focus:border-cyan focus:outline-none transition-colors"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Email</label>
              <input
                type="email" required value={form.email} onChange={update("email")}
                data-testid="contact-email-input"
                className="w-full px-4 py-3 rounded-lg bg-surface border border-white/10 text-white placeholder-slate-600 focus:border-cyan focus:outline-none transition-colors"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Message</label>
              <textarea
                required value={form.message} onChange={update("message")} rows={5} maxLength={3000}
                data-testid="contact-message-input"
                className="w-full px-4 py-3 rounded-lg bg-surface border border-white/10 text-white placeholder-slate-600 focus:border-cyan focus:outline-none transition-colors resize-y"
                placeholder="How can we help?"
              />
            </div>

            {error && <p data-testid="contact-error" className="text-sm text-fail">{error}</p>}

            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="contact-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm hover:brightness-110 disabled:opacity-60 transition-all"
            >
              <Send size={16} /> {status === "sending" ? "Sending…" : "Send message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
